import { isAxiosError } from "axios";

export const APP_ERROR_EVENT = "pde:app-error";

export type AppErrorKind = "api" | "job" | "unexpected";

export interface AppErrorNotice {
  id: string;
  kind: AppErrorKind;
  title: string;
  message: string;
  detail?: string;
  status?: number;
  path?: string;
  timestamp: string;
  fingerprint: string;
}

const notifiedErrors = new WeakSet<object>();

function objectMessage(value: Record<string, unknown>): string | null {
  const message = value.message;
  if (typeof message === "string" && message.trim()) return message;
  const detail = value.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  return null;
}

function detailMessage(detail: unknown): string | null {
  if (typeof detail === "string" && detail.trim()) return detail;
  if (Array.isArray(detail)) {
    const messages = detail
      .map((item) =>
        item && typeof item === "object"
          ? objectMessage(item as Record<string, unknown>)
          : String(item),
      )
      .filter(Boolean);
    return messages.length ? messages.join("; ") : null;
  }
  if (detail && typeof detail === "object") {
    return objectMessage(detail as Record<string, unknown>);
  }
  return null;
}

function responseMessage(data: unknown): string | null {
  if (!data) return null;
  if (typeof data === "string") return data;
  if (typeof data === "object") {
    const record = data as Record<string, unknown>;
    return detailMessage(record.detail) ?? objectMessage(record);
  }
  return null;
}

function methodAndPath(error: unknown): { method?: string; path?: string } {
  if (!isAxiosError(error)) return {};
  const method = error.config?.method?.toUpperCase();
  const path = error.config?.url;
  return { method, path };
}

export function wasErrorNotified(error: unknown): boolean {
  return !!error && typeof error === "object" && notifiedErrors.has(error);
}

function markErrorNotified(error: unknown): void {
  if (error && typeof error === "object") {
    notifiedErrors.add(error);
  }
}

function newNotice(input: Omit<AppErrorNotice, "id" | "timestamp" | "fingerprint">): AppErrorNotice {
  const timestamp = new Date().toISOString();
  const fingerprint = [
    input.kind,
    input.status ?? "",
    input.path ?? "",
    input.title,
    input.message,
  ].join("|");
  return {
    ...input,
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    timestamp,
    fingerprint,
  };
}

export function publishAppError(
  input: Omit<AppErrorNotice, "id" | "timestamp" | "fingerprint">,
  originalError?: unknown,
): void {
  markErrorNotified(originalError);
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent<AppErrorNotice>(APP_ERROR_EVENT, {
      detail: newNotice(input),
    }),
  );
}

export function notifyApiError(error: unknown): void {
  if (wasErrorNotified(error)) return;
  if (!isAxiosError(error)) {
    notifyUnexpectedError(error, "Erro de comunicacao");
    return;
  }

  const status = error.response?.status;
  const { method, path } = methodAndPath(error);
  const apiMessage = responseMessage(error.response?.data);
  let title = "Falha na API";
  let message =
    apiMessage ?? "A operacao nao foi concluida. O resultado desta etapa nao deve ser considerado valido.";

  if (error.code === "ECONNABORTED") {
    title = "Tempo limite da API";
    message = "A API demorou mais que o esperado para responder. Tente novamente antes de confiar no resultado.";
  } else if (!error.response) {
    title = "API indisponivel";
    message = "Nao foi possivel conectar com a API. Verifique disponibilidade antes de continuar o teste.";
  } else if (status && status >= 500) {
    title = "Erro interno da API";
    message = apiMessage ?? "A API retornou erro interno. Esta execucao nao deve ser usada para validacao.";
  } else if (status === 401) {
    title = "Acesso recusado pela API";
    message = apiMessage ?? "A API recusou a chamada. Verifique a integracao externa antes de continuar.";
  } else if (status === 403) {
    title = "Acesso negado";
    message = apiMessage ?? "A API recusou esta operacao.";
  } else if (status === 404) {
    title = "Registro nao encontrado";
    message = apiMessage ?? "A API nao encontrou o recurso solicitado.";
  } else if (status === 409) {
    title = "Operacao bloqueada";
  } else if (status === 422) {
    title = "Dados recusados pela API";
  }

  publishAppError(
    {
      kind: "api",
      title,
      message,
      detail: [method, path].filter(Boolean).join(" "),
      status,
      path,
    },
    error,
  );
}

export function notifyJobError(job: {
  id: string;
  job_type?: string;
  error_message?: string | null;
}): void {
  publishAppError({
    kind: "job",
    title: "Processamento interrompido",
    message:
      job.error_message ??
      "Um processamento em segundo plano falhou. O resultado desta etapa nao deve ser considerado valido.",
    detail: [job.job_type, job.id].filter(Boolean).join(" "),
  });
}

export function notifyUnexpectedError(error: unknown, context = "Interface"): void {
  if (wasErrorNotified(error)) return;
  const message =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : "Ocorreu uma falha inesperada na interface.";
  publishAppError(
    {
      kind: "unexpected",
      title: "Erro inesperado",
      message,
      detail: context,
    },
    error,
  );
}
